// src/components/ui/AvatarPicker.jsx - Updated for public folder
import React, { useState, useEffect } from 'react';
import { getCustomAvatars } from '../../utils/avatar'; // Import from utility

const AvatarPicker = ({ currentAvatar, onAvatarSelect, onClose }) => {
  const [selectedAvatar, setSelectedAvatar] = useState(currentAvatar);
  const [avatarOptions, setAvatarOptions] = useState([]);

  useEffect(() => {
    // Use the centralized avatar data from utils
    setAvatarOptions(getCustomAvatars());
  }, []);

  const handleSelectAvatar = (avatar) => {
    setSelectedAvatar(avatar.url);
  };

  const handleConfirmSelection = () => {
    if (selectedAvatar && onAvatarSelect) {
      onAvatarSelect(selectedAvatar);
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Choose Your Avatar</h3>
          <p className="text-gray-600">Select an avatar style that represents you</p>
        </div>

        {/* Avatar Grid */}
        <div className="p-6 overflow-y-auto max-h-96">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
            {avatarOptions.map((avatar) => (
              <button
                key={avatar.id}
                onClick={() => handleSelectAvatar(avatar)}
                className={`relative group transition-all duration-200 ${
                  selectedAvatar === avatar.url
                    ? 'ring-2 ring-blue-500'
                    : 'hover:ring-2 hover:ring-gray-300'
                } rounded-xl overflow-hidden`}
              >
                <img
                  src={avatar.url}
                  alt={avatar.name}
                  className="w-full h-full object-cover rounded-xl"
                  onError={(e) => {
                    // Fallback if image fails to load
                    console.warn(`Failed to load avatar: ${avatar.url}`);
                    e.target.src = `https://placehold.co/128x128/3b82f6/ffffff?text=${avatar.name.charAt(0)}`;
                  }}
                />

                {/* Selected indicator */}
                {selectedAvatar === avatar.url && (
                  <div className="absolute top-2 right-2">
                    <div className="bg-blue-500 text-white rounded-full p-1.5 shadow-lg">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                )}

                {/* Avatar name */}
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs p-2 transform translate-y-full group-hover:translate-y-0 transition-transform duration-200">
                  {avatar.name}
                </div>
              </button>
            ))}
          </div>

          {/* Current Avatar Preview */}
          {selectedAvatar && (
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 mb-3">Preview:</p>
              <img
                src={selectedAvatar}
                alt="Selected avatar"
                className="w-20 h-20 rounded-full mx-auto border-4 border-blue-200"
                onError={(e) => {
                  e.target.src = `https://placehold.co/80x80/3b82f6/ffffff?text=?`;
                }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmSelection}
            disabled={!selectedAvatar}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Select Avatar
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvatarPicker;